<template>
  <NuxtLoadingIndicator color="#8aaf18" />
  <ClientOnly>
    <GlobalBanner v-if="marketingBannerEnabled" />
  </ClientOnly>
  <PageHeader />
  <main>
    <div v-show="!isHidden" class="z-30 sticky top-[54px] sm:hidden">
      <Search v-bind="{ showBackdrop: true, showCloseButton: false, isInModal: false }" />
    </div>
    <ToastContainer />
    <MobileDrawer />
    <slot />
    <CartDrawer />
  </main>
  <Chatbot v-if="enableBotfriends && localeConfig.code === 'de'" />
  <PageFooter />
  <CountryBar />
  <GlobalLoginModal />
  <ScreenIndicator />
  <div
    v-if="loyaltyInfo?.signature"
    id="swell-customer-identification"
    data-authenticated="true"
    :data-email="loyaltyInfo.email ?? ''"
    :data-id="loyaltyInfo.customerId ?? ''"
    :data-token="loyaltyInfo.signature ?? ''"
    data-tags="[]"
    style="display: none"
  />
</template>

<script setup async>
import CartDrawer from '@/components/layout/CartDrawer';
import Chatbot from '@/components/layout/Chatbot';
import CountryBar from '@/components/layout/CountryBar';
import GlobalLoginModal from '@/components/account/GlobalLoginModal';
import GlobalBanner from '@/components/layout/GlobalBanner';
import MobileDrawer from '@/components/layout/header/mobileDrawer/MobileDrawer';
import PageFooter from '@/components/layout/footer/PageFooter';
import PageHeader from '@/components/layout/header/PageHeader';
import ScreenIndicator from '@/components/layout/ScreenIndicator';
import Search from '@/components/search/Search';
import ToastContainer from '@/components/layout/ToastContainer';
import { scrollToSelectorRespectingBars } from '@/utils/common';
import { useSearchStore } from '@/stores/search';
import { useGlobalBannerStore } from '@/stores/global-banner';
import { computed, nextTick, onMounted } from 'vue';
import { injectSpeedInsights, computeRoute } from '@vercel/speed-insights';

const dnsPrefetchHrefs = [
  'https://cldn.cdn-blackroll.com',
  'https://shop.de.blackroll.com',
  'https://cdn.cookielaw.org',
  'https://www.googletagmanager.com',
];

const dnsPreconnectHrefs = [
  { url: 'https://cdn.cookielaw.org', crossorigin: true },
  { url: 'https://www.googleoptimize.com', crossorigin: false },
  { url: 'https://cldn.cdn-blackroll.com', crossorigin: false },
  { url: 'https://cdn-widgetsrepository.yotpo.com', crossorigin: true },
];

const router = useRouter();
const route = useRoute();
const searchStore = useSearchStore();
const globalBannerStore = useGlobalBannerStore();
const { $getCurrentLocaleConfig } = useNuxtApp();
const { enableBotfriends } = useRuntimeConfig().public;

const isMac = ref(false);
const spi = ref(injectSpeedInsights({ sampleRate: 0.1, framework: 'nuxt' }));
const localeConfig = $getCurrentLocaleConfig();

const isHidden = computed(() => searchStore.isHidden);
const marketingBannerEnabled = computed(() => globalBannerStore.marketingBannerEnabled);

const config = useRuntimeConfig();

const { loggedIn } = useUserSession();
const requestFetch = useRequestFetch();

const { data: loyaltyInfo } = await useAsyncData(
  'loyalty-info',
  async () => {
    if (!loggedIn.value) return null;
    try {
      const response = await requestFetch('/api/user/loyaltyInfo');
      return response.data ?? null;
    } catch (e) {
      if (e?.statusCode !== 401) {
        console.error('[swell-loyalty-info]', e);
      }
      return null;
    }
  },
  { watch: [loggedIn] }
);

const isStaging = ref(config.public.app_env === 'staging');
const isDevelopment = ref(config.public.app_env === 'development');

const faviconLinks = computed(() => {
  const variant = isStaging.value ? '/favicon-staging' : isDevelopment.value ? '/favicon-dev' : ''; // production default

  return [
    { rel: 'icon', type: 'image/png', href: `${variant}/favicon-96x96.png`, sizes: '96x96' },
    { rel: 'icon', type: 'image/svg+xml', href: `${variant}/favicon.svg` },
    { rel: 'shortcut icon', href: `${variant}/favicon.ico` },
    { rel: 'apple-touch-icon', sizes: '180x180', href: `${variant}/apple-touch-icon.png` },
    { rel: 'manifest', href: `${variant}/site.webmanifest` },
  ];
});

useHead(() => {
  return {
    htmlAttrs: {
      lang: localeConfig.language,
      class: isMac.value ? 'mac-os' : '',
    },
    meta: [
      { charset: 'utf-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1, maximum-scale=1' },
      { name: 'apple-mobile-web-app-title', content: 'BLACKROLL' },
      { name: 'p:domain_verify', content: 'b6bf0a8b9bcb6c537d0c7d1b178348df' },
    ],
    link: [
      ...faviconLinks.value,
      ...dnsPreconnectHrefs.map((item) => ({ rel: 'preconnect', href: item.url, crossorigin: item.crossorigin })),
      ...dnsPrefetchHrefs.map((href) => ({ rel: 'dns-prefetch', href })),
    ],
    style: [{ cssText: '.async-hide { opacity: 0 !important}' }],
  };
});

const changeRoute = () => {
  spi.value.setRoute(computeRoute(route.path, route.params));
};

const checkIfMac = () => {
  // should be computed but doesn't re-trigger when detects browser type
  isMac.value = import.meta.browser && navigator.userAgent?.indexOf('Mac OS X') !== -1;
};

const onRouteComplete = () => {
  nextTick(function () {
    const hash = route.hash;
    if (!hash) return;
    setTimeout(() => {
      scrollToSelectorRespectingBars(hash);
    }, 2000);
  });
};

if (route && spi.value) {
  changeRoute();
}

onMounted(() => {
  onRouteComplete();
  checkIfMac();
});

router.afterEach(onRouteComplete);
</script>
